import React, { useEffect, useMemo, useState } from 'react';
import comms from '../comms';
import BottomSheet from '../ui/BottomSheet';
import TimeStepper from '../ui/TimeStepper';
import Toggle from '../ui/Toggle';
import LocationPicker from './LocationPicker';
import { toTemplateTime, toTimeInputValue, WEEKDAY_KEYS } from './schedulingUtils';

// RouteMaster template editor (spec §4.3/§4.4): weekday start/end/break times,
// start/end locations, and the whole-template enable/disable. PUT sends only
// the fields the driver actually changed; a weekday with start/end null means
// "does not run that day" (no baseline route generated).

const DAY_LABELS = {
  sunday: 'Sunday',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
};
const WORKWEEK = ['tuesday', 'wednesday', 'thursday', 'friday'];
const DEFAULT_DAY = { start: '08:00', end: '16:00', breakStart: '', breakEnd: '' };

// Template day ("HH:MM:SS" | null fields) → editable draft ("HH:MM" strings).
function toDraftDay(day) {
  return {
    start: toTimeInputValue(day?.start),
    end: toTimeInputValue(day?.end),
    breakStart: toTimeInputValue(day?.breakStart),
    breakEnd: toTimeInputValue(day?.breakEnd),
  };
}

function TemplateEditor({ open, onClose, template, driverId, online, onSaved, onToast }) {
  const original = useMemo(() => {
    const days = {};
    WEEKDAY_KEYS.forEach((k) => {
      days[k] = toDraftDay(template?.days?.[k]);
    });
    return {
      days,
      isInactive: template?.isInactive === true,
      startVehicleLocationId: template?.startVehicleLocationId || null,
      endVehicleLocationId: template?.endVehicleLocationId || null,
    };
  }, [template]);

  const [days, setDays] = useState(original.days);
  const [isInactive, setIsInactive] = useState(original.isInactive);
  const [startLoc, setStartLoc] = useState({
    id: original.startVehicleLocationId,
    name: template?.startLocationName || '',
  });
  const [endLoc, setEndLoc] = useState({
    id: original.endVehicleLocationId,
    name: template?.endLocationName || '',
  });
  const [expanded, setExpanded] = useState(null); // weekday key
  const [pickerFor, setPickerFor] = useState(null); // 'start' | 'end' | null
  const [saving, setSaving] = useState(false);

  // Re-seed the draft whenever the sheet opens for a (re)loaded template.
  useEffect(() => {
    if (!open) return;
    setDays(original.days);
    setIsInactive(original.isInactive);
    setStartLoc({ id: original.startVehicleLocationId, name: template?.startLocationName || '' });
    setEndLoc({ id: original.endVehicleLocationId, name: template?.endLocationName || '' });
    setExpanded(null);
  }, [open, original, template]);

  const setDay = (key, patch) => {
    setDays((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const toggleDay = (key, on) => {
    if (on) {
      const cur = days[key];
      setDay(key, cur.start && cur.end ? {} : { ...DEFAULT_DAY });
      setExpanded(key);
    } else {
      setDay(key, { start: '', end: '', breakStart: '', breakEnd: '' });
      if (expanded === key) setExpanded(null);
    }
  };

  const applyMonToWeek = () => {
    const mon = days.monday;
    setDays((prev) => {
      const next = { ...prev };
      WORKWEEK.forEach((k) => {
        next[k] = { ...mon };
      });
      return next;
    });
    onToast('Monday hours applied to Tue–Fri.');
  };

  const handleSave = async () => {
    if (!online) {
      onToast('Connect to save your template.');
      return;
    }
    // Diff against the loaded template — only changed fields go in the body.
    const body = { driverId, routeMasterId: template?.id };
    const changedDays = {};
    WEEKDAY_KEYS.forEach((k) => {
      const a = original.days[k];
      const b = days[k];
      if (
        a.start !== b.start ||
        a.end !== b.end ||
        a.breakStart !== b.breakStart ||
        a.breakEnd !== b.breakEnd
      ) {
        changedDays[k] = {
          start: toTemplateTime(b.start),
          end: toTemplateTime(b.end),
          breakStart: toTemplateTime(b.breakStart),
          breakEnd: toTemplateTime(b.breakEnd),
        };
      }
    });
    if (Object.keys(changedDays).length) body.days = changedDays;
    if (isInactive !== original.isInactive) body.isInactive = isInactive;
    if ((startLoc.id || null) !== original.startVehicleLocationId)
      body.startVehicleLocationId = startLoc.id || null;
    if ((endLoc.id || null) !== original.endVehicleLocationId)
      body.endVehicleLocationId = endLoc.id || null;

    if (!body.days && body.isInactive === undefined &&
        body.startVehicleLocationId === undefined && body.endVehicleLocationId === undefined) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      const res = await comms.updateRouteMasterTemplate(body);
      if (res?.status === true) {
        onToast('Template saved — future routes will use it.');
        onSaved();
        onClose();
      } else {
        onToast(res?.error || res?.message || "Couldn't save template, try again.");
      }
    } catch (e) {
      onToast(e?.message || "Couldn't save template, try again.");
    } finally {
      setSaving(false);
    }
  };

  const locButton = (label, loc, field) => (
    <button
      type="button"
      onClick={() => setPickerFor(field)}
      className="flex min-h-[44px] w-full items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-left"
    >
      <span className="text-sm text-gray-500">{label}</span>
      <span className="ml-2 truncate text-sm font-medium text-gray-800">
        {loc.name || 'Choose…'}
      </span>
    </button>
  );

  return (
    <>
      <BottomSheet
        open={open}
        onClose={onClose}
        title="My route template"
        footer={
          <button
            onClick={handleSave}
            disabled={saving || !online}
            className="w-full rounded-xl bg-brand px-4 py-3 text-sm font-semibold text-white transition-transform active:scale-95 disabled:opacity-60"
          >
            {saving ? 'Saving…' : !online ? 'Offline' : 'Save template'}
          </button>
        }
      >
        <div className="space-y-3">
          {/* Whole-template enable/disable (stops future baseline generation) */}
          <div className="flex items-center justify-between rounded-2xl bg-gray-50 px-3 py-2">
            <div>
              <div className="text-sm font-medium text-gray-800">
                {template?.name || 'My route'}
              </div>
              <div className="text-xs text-gray-500">
                {isInactive ? 'Disabled — no routes will be generated' : 'Active'}
              </div>
            </div>
            <Toggle checked={!isInactive} onChange={(on) => setIsInactive(!on)} />
          </div>

          {/* Start/end locations */}
          <div className="space-y-2">
            {locButton('Starts at', startLoc, 'start')}
            {locButton('Ends at', endLoc, 'end')}
          </div>

          {/* Weekday rows */}
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <span className="text-sm font-bold text-gray-700">Weekly hours</span>
              <button
                type="button"
                onClick={applyMonToWeek}
                disabled={!days.monday.start || !days.monday.end}
                className="text-xs font-semibold text-brand disabled:opacity-40"
              >
                Apply Mon → Tue–Fri
              </button>
            </div>

            {WEEKDAY_KEYS.map((key) => {
              const day = days[key];
              const on = !!(day.start && day.end);
              const isOpen = expanded === key;
              return (
                <div key={key} className="rounded-2xl border border-gray-100 bg-white shadow-sm">
                  <div className="flex items-center justify-between px-3 py-1.5">
                    <button
                      type="button"
                      onClick={() => on && setExpanded(isOpen ? null : key)}
                      className="min-h-[44px] flex-1 text-left"
                    >
                      <span className="text-sm font-medium text-gray-800">{DAY_LABELS[key]}</span>
                      {on && (
                        <span className="ml-2 text-xs text-gray-500">
                          {day.start} – {day.end}
                          {day.breakStart ? ` · break ${day.breakStart}` : ''}
                        </span>
                      )}
                      {!on && <span className="ml-2 text-xs text-gray-400">Off</span>}
                    </button>
                    <Toggle checked={on} onChange={(next) => toggleDay(key, next)} />
                  </div>

                  {on && isOpen && (
                    <div className="space-y-2 border-t border-gray-100 px-3 py-3">
                      <TimeStepper label="Start" value={day.start} onChange={(v) => setDay(key, { start: v })} />
                      <TimeStepper label="End" value={day.end} onChange={(v) => setDay(key, { end: v })} />
                      <TimeStepper
                        label="Break"
                        value={day.breakStart}
                        onChange={(v) => setDay(key, { breakStart: v })}
                      />
                      <TimeStepper
                        label="until"
                        value={day.breakEnd}
                        onChange={(v) => setDay(key, { breakEnd: v })}
                      />
                      {(day.breakStart || day.breakEnd) && (
                        <button
                          type="button"
                          onClick={() => setDay(key, { breakStart: '', breakEnd: '' })}
                          className="text-xs font-semibold text-danger"
                        >
                          Clear break
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </BottomSheet>

      <LocationPicker
        open={!!pickerFor}
        onClose={() => setPickerFor(null)}
        driverId={driverId}
        online={online}
        onToast={onToast}
        onSelect={(loc) => {
          const sel = { id: loc.Id, name: loc.Name };
          if (pickerFor === 'start') setStartLoc(sel);
          else setEndLoc(sel);
          setPickerFor(null);
        }}
      />
    </>
  );
}

export default TemplateEditor;
